<script setup>
import { ref, onMounted, watch } from 'vue';
import axios from 'axios';
// 💡 REQUIRED: Import the xlsx library (Ensure you run 'npm install xlsx')
import * as XLSX from 'xlsx';
import { API_BASE_URL } from '@/config'; 

// We'll store categories as objects { value: '...', text: '...' } for the dropdown
const categories = ref([]);
const assets = ref([]);
const selectedCategory = ref('');
const loading = ref(false);
const error = ref(null);
const notification = ref({
  show: false,
  message: '',
  type: ''
});


const categoriesApiUrl = `${API_BASE_URL}/api/assetcategories`;
const assetsByCategoryApiUrl = `${API_BASE_URL}/api/reports/assets/by-category`;

// Fetch categories for the dropdown
const fetchCategories = async () => {
    try {
        const response = await axios.get(categoriesApiUrl);
        categories.value = response.data.map(item => ({
            value: item.asset_cat_no,
            text: `${item.asset_cat_desc} (${item.asset_cat_no})`
        }));

        if (categories.value.length === 0) {
             showNotification('No asset categories available.', 'warning');
        }
    } catch (err) {
        console.error('Error fetching asset categories:', err);
        showNotification('Could not load asset categories list.', 'error');
    }
};

// Fetch assets filtered by the selected Category
const fetchAssetsByCategory = async (category) => {
    if (!category) {
        assets.value = []; // Clear assets if no category is selected
        return;
    }
    loading.value = true;
    error.value = null;
    try {
        const response = await axios.get(`${assetsByCategoryApiUrl}?category=${encodeURIComponent(category)}`);
        assets.value = response.data;
    } catch (err) {
        console.error(`Error fetching assets for Category ${category}:`, err);
        error.value = 'Failed to load assets for the selected category.';
         if (err.response?.status === 400) {
             error.value = err.response.data || 'Please select a valid category.';
         } else if (err.response?.status === 503) {
              error.value = 'Database service unavailable.';
         } else {
              error.value = 'Server Error fetching assets.';
         }
        showNotification(error.value, 'error');
         assets.value = []; // Clear previous results on error
    } finally {
        loading.value = false;
    }
};

// Watch for changes in selectedCategory and fetch assets
watch(selectedCategory, (newValue) => {
    fetchAssetsByCategory(newValue);
});

// Show notification helper
const showNotification = (message, type) => {
  notification.value.message = message;
  notification.value.type = type;
  notification.value.show = true;
  setTimeout(() => {
    notification.value.show = false;
  }, 5000);
};


// Fetch initial data on component mount
onMounted(() => {
    fetchCategories(); // Fetch the list of categories when the component mounts
});


// Helper to format dates
const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
     if (isNaN(date)) return '-'; // Handle invalid dates
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return date.toLocaleDateString(undefined, options);
};

const formatCurrency = (value) => {
    if (value === null || value === undefined) return '-';
    return parseFloat(value).toFixed(2); // Keeping as number string for Excel
};

// --- WORKING EXCEL EXPORT FUNCTION ---
const exportToExcel = () => {
    if (assets.value.length === 0) {
        showNotification('No assets to export.', 'warning');
        return;
    }

    // 1. Prepare data for export, applying formatting like currency and date
    const dataToExport = assets.value.map(asset => ({
        'Asset No': asset.asset_no,
        'Description': asset.asset_desc,
        'Location': asset.loc_no,
        'Department': asset.dep_no,
        // 'Category': asset.category,
        // 'Status': asset.status,
        // 'Criticality': asset.criticality,
        // 'Manufacturer': asset.manf,
        // 'Model': asset.model_no,
        // 'Serial No': asset.serial_no,
        'Date Acquired': formatDate(asset.date_aquired), // Formatted date
        // 'Current Value': formatCurrency(asset.current_value), // Formatted currency (as number string)
        'Next Sch': formatDate(asset.Next_Sch), // Formatted date
        'Site Code': asset.SiteCode,
    }));

    // 2. Create a worksheet from the JSON data
    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    
    // 3. Create a workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Assets Report');

    // 4. Determine file name
    const categoryName = categories.value.find(c => c.value === selectedCategory.value)?.text.split('(')[0].trim() || 'All';
    const fileName = `AssetReport_${categoryName}_${new Date().toISOString().slice(0, 10)}.xlsx`;

    // 5. Write and download the file
    XLSX.writeFile(workbook, fileName);
    showNotification(`Exported ${assets.value.length} assets to Excel!`, 'success');
};

// --- WORKING PDF/PRINT FUNCTION ---
// --- ENHANCED PDF/PRINT FUNCTION ---
const printPDF = () => {
    if (assets.value.length === 0) {
        showNotification('No data to print.', 'warning');
        return;
    }
    
    // Store original display data for print
    const printData = {
        title: 'Assets by Category Report',
        category: selectedCategory.value ? categories.value.find(c => c.value === selectedCategory.value)?.text.split('(')[0].trim() : 'All Categories',
        assets: assets.value,
        totalRecords: assets.value.length
    };
    
    // Store in session storage for print window to access
    sessionStorage.setItem('printData', JSON.stringify(printData));
    
    // Open print window
    const printWindow = window.open('', '_blank');
    
    // Get the base URL for logo paths
    const baseUrl = window.location.origin;
    
    printWindow.document.write(`
    <!DOCTYPE html>
    <html>
    <head>
        <title>Assets by Category Report</title>
        <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .print-header { 
                display: flex; 
                justify-content: space-between; 
                align-items: center; 
                margin-bottom: 30px; 
                border-bottom: 2px solid #333; 
                padding-bottom: 10px; 
            }
            .logo-left { 
                flex: 1; 
                text-align: left; 
            }
            .logo-right { 
                flex: 1; 
                text-align: right; 
            }
            .logo-img { 
                max-height: 80px; 
                max-width: 150px; 
            }
            .logo-img2 { 
                max-height: 120px; 
                max-width: 120px; 
            }
            .header-center { 
                flex: 2; 
                text-align: center; 
            }
            .print-title { font-size: 24px; font-weight: bold; margin-bottom: 10px; }
            .print-filters { margin-bottom: 20px; font-size: 14px; color: #666; }
            .print-table { width: 100%; border-collapse: collapse; font-size: 12px; }
            .print-table th, .print-table td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            .print-table th { background-color: #f5f5f5; font-weight: bold; }
            .print-table tr:nth-child(even) { background-color: #f9f9f9; }
            @media print {
                body { margin: 0; }
                .print-header { margin-bottom: 20px; }
                .logo-img { max-height: 70px; max-width: 120px; }
                .logo-img2 { max-height: 150px; max-width: 120px; }
                .print-table { font-size: 10px; }
                .print-table th, .print-table td { padding: 6px; }
            }
        </style>
    </head>
    <body>
        <!-- Header with Two Logos -->
        <div class="print-header">
            <div class="logo-left">
                <!-- Left Logo - Replace with your actual logo path -->
                <img src="../logo.png" alt="Company Logo Left" class="logo-img">
            </div>
            <div class="header-center">
                <div class="print-title">Assets by Category Report</div>
                <div class="print-filters">
                    Report Date: ${new Date().toLocaleDateString()} | 
                    Category: ${printData.category} | 
                    Total Records: ${printData.totalRecords}
                </div>
            </div>
            <div class="logo-right">
                <!-- Right Logo - Replace with your actual logo path -->
                <img src="../logo2.png" alt="Company Logo Right" class="logo-img2">
            </div>
        </div>

        <table class="print-table">
            <thead>
                <tr>
                    <th>Asset No</th>
                    <th>Description</th>
                    <th>Location</th>
                    <th>Department</th>
                    <th>Next Sch</th>
                    <th>Site Code</th>
                </tr>
            </thead>
            <tbody>
                ${printData.assets.map(asset => `
                    <tr>
                        <td><strong>${asset.asset_no}</strong></td>
                        <td>${asset.asset_desc}</td>
                        <td>${asset.loc_no}</td>
                        <td>${asset.dep_no}</td>
                        <td>${formatDate(asset.Next_Sch)}</td>
                        <td>${asset.SiteCode}</td>
                    </tr>
                `).join('')}
            </tbody>
        </table>
        <script>
            window.onload = function() {
                window.print();
                setTimeout(() => window.close(), 500);
            };
        <\/script>
    </body>
    </html>
    `);
    printWindow.document.close();
};
// --- END ENHANCED PDF/PRINT FUNCTION ---
// --- END WORKING FUNCTIONS ---
</script>

<template>
  <div class="container mt-4">
    
    <div class="print-only-header">
        <!-- <img src="/path/to/your/website-logo.png" alt="Website Logo" class="print-logo">  -->
        <div class="print-date">Report Date: {{ formatDate(new Date()) }}</div>
    </div>
    
    <h2 class="report-title">Assets by Category Report</h2>

    <div class="d-flex justify-content-between align-items-end mb-4 flex-wrap print-hidden-element">
        
        <div class="filter-controls">
            <label for="categoryFilter" class="form-label">Select Category:</label>
            <select id="categoryFilter" class="form-select" v-model="selectedCategory" :disabled="loading || categories.length === 0">
                <option value="">-- Select a Category --</option>
                <option v-for="category in categories" :key="category.value" :value="category.value">
                    {{ category.text }}
                </option>
                 <option value="" disabled v-if="categories.length === 0 && !loading">Loading categories...</option>
                 <option value="" disabled v-if="categories.length === 0 && loading">No categories available</option>
            </select>
        </div>

        <div class="report-actions d-flex gap-2 mt-3 mt-sm-0" v-if="assets.length > 0 && !loading && !error">
            <button @click="exportToExcel" class="btn btn-success">
                <i class="bi bi-file-earmark-spreadsheet"></i> Export To Excel
            </button>
            <button @click="printPDF" class="btn btn-secondary">
                 <i class="bi bi-printer"></i> Print as PDF
            </button>
        </div>
    </div>
    <div v-if="loading" class="alert alert-info print-hidden-element">Loading assets...</div>
    <div v-if="error" class="alert alert-danger print-hidden-element">{{ error }}</div>
     <div v-if="notification.show" :class="['alert', `alert-${notification.type}`, 'mt-3', 'print-hidden-element']">
       {{ notification.message }}
     </div>


    <div v-if="!loading && !error">
        <div v-if="assets.length > 0" class="table-responsive">
            <table class="table table-striped table-hover report-table">
                <thead>
                    <tr>
                         <th>Asset No</th>
                         <th>Description</th>
                         <th>Location</th>
                         <th>Department</th>
                         <!-- <th>Category</th>
                         <th>Status</th>
                         <th>Criticality</th>
                         <th>Manufacturer</th>
                         <th>Model</th>
                         <th>Serial No</th> -->
                         <!-- <th>Date Acquired</th> -->
                         <!-- <th>Current Value</th> -->
                         <th>Next Sch</th>
                         <th>Site Code</th>
                         </tr>
                </thead>
                <tbody>
                    <tr v-for="asset in assets" :key="asset.asset_no">
                         <td>{{ asset.asset_no }}</td>
                         <td>{{ asset.asset_desc }}</td>
                         <td>{{ asset.loc_no }}</td>
                         <td>{{ asset.dep_no }}</td>
                         <!-- <td>{{ asset.category }}</td>
                         <td>{{ asset.status }}</td>
                         <td>{{ asset.criticality }}</td>
                         <td>{{ asset.manf }}</td>
                         <td>{{ asset.model_no }}</td>
                         <td>{{ asset.serial_no }}</td> -->
                         <!-- <td>{{ formatDate(asset.date_aquired) }}</td> -->
                         <!-- <td>{{ formatCurrency(asset.current_value) }}</td> -->
                         <td>{{ formatDate(asset.Next_Sch) }}</td>
                         <td>{{ asset.SiteCode }}</td>
                         </tr>
                </tbody>
            </table>
        </div>
         <div v-else-if="selectedCategory && !loading">
            <p>No assets found for the selected category.</p>
        </div>
         <div v-else-if="!selectedCategory && categories.length > 0 && !loading">
             <p>Please select a category from the dropdown above.</p>
         </div>
         <div v-else-if="!loading">
             <p>No assets loaded.</p>
         </div>
    </div>

  </div>
</template>

<style scoped>
/* Ensure you have Bootstrap linked for d-flex and form-select styles to work */
.filter-controls {
    max-width: 300px; /* Limit the width of the filter dropdown */
}

/* Default state: Hide print-only elements on screen */
.print-only-header {
    display: none;
}

/* --- REFINED PRINT STYLES FOR CLEAN PDF OUTPUT --- */
@media print {
    /* **1. Hide All UI/Controls (Including the Navbar and Global Header) and Show the Logo** */
    
    /* CUSTOMIZE THIS: Use this block to hide the SOULMAINT header/logo/date shown in your image. 
       Inspect the element in the browser to find the correct selector for the container holding:
       - SOULMAINT logo
       - Gear icon
       - Empty white box
       - Date/Time
       - "frontend" text
       
       We'll try a common selector like a main app header ID/class, e.g., #app-header or #main-layout > header.
    */
    #app-header-area, /* Example: If your main header has this ID */
    .app-header, /* Example: If your main header has this class */
    .navbar, /* Hide common navbars */
    .print-hidden-element,
    .filter-controls,
    .report-actions,
    .alert {
        display: none !important;
    }
    .navbar {
        display: none !important;
    }


    /* Show the print-only header */
    .print-only-header {
        display: block !important;
        text-align: center;
        margin-bottom: 20px;
    }
    
    /* Style the logo */
    .print-logo {
        max-width: 150px; /* Adjust size as needed */
        height: auto;
    }
    .print-date {
        margin-top: 5px;
        font-size: 10pt;
    }
    
    /* **2. Clean up the Report Layout and Title** */
    
    /* Center and style the report title beneath the logo */
    .report-title {
        display: block !important;
        text-align: center;
        margin-top: 20px;
        margin-bottom: 30px;
        font-size: 18pt;
        color: black !important;
    }

    /* Target the container to ensure full width and no margins */
    .container {
        width: 100%;
        max-width: none;
        margin: 0;
        padding: 0 10px; /* Small padding for edges */
        box-shadow: none;
    }
    
    /* **3. Optimize Table Display** */
    .table-responsive {
        overflow: visible !important;
    }
    .report-table {
        font-size: 9pt; /* Smaller font for more data on the page */
        width: 100% !important;
        table-layout: fixed;
        border-collapse: collapse;
    }
    .report-table th, .report-table td {
        padding: 4px;
        border: 1px solid #ccc; /* Add borders for report clarity */
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
    }
    .table-striped tbody tr:nth-of-type(odd) {
        background-color: #f2f2f2 !important;
    }
    .table-hover tbody tr:hover {
        background-color: transparent !important;
    }
}



</style>